/**
 * Cloudflare Turnstile in the browser (docs/turnstile.md): the script, loaded
 * once and only when a site key is configured, and the slice of its API used.
 */

/** Public by design: it names the widget, not a secret. Absent, the panel sends no token. */
export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? '';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export interface TurnstileRenderOptions {
  readonly sitekey: string;
  readonly action: string;
  readonly appearance: 'always' | 'execute' | 'interaction-only';
  readonly 'refresh-expired': 'auto' | 'manual' | 'never';
  readonly callback: (token: string) => void;
  readonly 'expired-callback': () => void;
  readonly 'error-callback': () => void;
}

export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi> | undefined;

/** One script tag however many widgets ask; a failed load may be tried again. */
export function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile !== undefined) return Promise.resolve(window.turnstile);
  loading ??= new Promise<TurnstileApi>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () =>
      window.turnstile === undefined
        ? reject(new Error('turnstile did not load'))
        : resolve(window.turnstile);
    script.onerror = () => reject(new Error('turnstile did not load'));
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = undefined;
    throw error;
  });
  return loading;
}
